import React from 'react';
import { format, isValid, parseISO } from "date-fns";
import { DateUtil } from "@/utils/dateUtil.ts";
import { formatDate, formatDateTime } from "@/lib/formatDate";
import { displayAge } from "@/lib/patientAge";
import { groupPhone, Letterhead, PrintFooter, PrintStyles, SignatureBlock } from "@/components/Print/PrintParts";
import { doseMeaning } from "@/components/Print/doseText";
import { examSigns } from "@/lib/vitals";
import type { HeaderConfig } from '@/components/Print/UnifiedPrintModal';

interface PrescriptionContentProps {
  data: any;
  translations: any;
  language: string;
  clinicInfo: any;
  doctorInfo: any;
  patientInfo: any;
  settings: any;
  currentUser: any;
  headerConfig?: HeaderConfig;
  /** The doctor's signature image (data URL), printed above the signature line */
  signatureImage?: string | null;
}

export function PrescriptionContent({
                                      data,
                                      translations: t,
                                      language,
                                      clinicInfo,
                                      doctorInfo,
                                      patientInfo,
                                      settings,
                                      headerConfig,
                                      signatureImage,
                                    }: Readonly<PrescriptionContentProps>) {

  // ── Helpers ──────────────────────────────────────────────────────────────────

  const normalizeText = (value: unknown): string => {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  };

  const hasText = (value: unknown): boolean => normalizeText(value).length > 0;

  // ── Translation map ───────────────────────────────────────────────────────────

  const phraseMap: Record<string, string> = {
    'after breakfast':  t.afterBreakfast  ?? 'After Breakfast',
    'before breakfast': t.beforeBreakfast ?? 'Before Breakfast',
    'with breakfast':   t.withBreakfast   ?? 'With Breakfast',
    'after lunch':      t.afterLunch      ?? 'After Lunch',
    'before lunch':     t.beforeLunch     ?? 'Before Lunch',
    'with lunch':       t.withLunch       ?? 'With Lunch',
    'after dinner':     t.afterDinner     ?? 'After Dinner',
    'before dinner':    t.beforeDinner    ?? 'Before Dinner',
    'with dinner':      t.withDinner      ?? 'With Dinner',
    'after food':       t.afterFood       ?? 'After Food',
    'before food':      t.beforeFood      ?? 'Before Food',
    'with food':        t.withFood        ?? 'With Food',
    'empty stomach':    t.emptyStomach    ?? 'Empty Stomach',
    'at bedtime':       t.bedtime         ?? 'At Bedtime',
    'bedtime':          t.bedtime         ?? 'At Bedtime',
    'morning':          t.morning         ?? 'Morning',
    'afternoon':        t.afternoon       ?? 'Afternoon',
    'evening':          t.evening         ?? 'Evening',
    'night':            t.night           ?? 'Night',
    'as directed':      t.asDirected      ?? 'As Directed',
    'sos':              t.sos             ?? 'SOS',
    'days':             t.days            ?? 'days',
    'day':              t.day             ?? 'day',
    'weeks':            t.weeks           ?? 'weeks',
    'week':             t.week            ?? 'week',
    'months':           t.months          ?? 'months',
    'month':            t.month           ?? 'month',
  };

  // The pad's multi-word When / Duration options, translated as a whole — word by word gave "on alternate दिवस"
  const WHOLE_PHRASES: Record<string, Record<string, string>> = {
    mr: {
      'every 8 hours': 'दर 8 तासांनी', 'every 12 hours': 'दर 12 तासांनी',
      'on alternate days': 'एक दिवसाआड', 'weekly': 'आठवड्यातून एकदा', 'twice weekly': 'आठवड्यातून दोनदा',
      'day 1-5 of cycle': 'पाळीच्या 1 ते 5 व्या दिवशी', 'day 2-6 of cycle': 'पाळीच्या 2 ते 6 व्या दिवशी', 'day 21 of cycle': 'पाळीच्या 21 व्या दिवशी',
      'continuously': 'सतत', 'as needed': 'गरजेनुसार',
      'before physiotherapy': 'फिजिओथेरपीपूर्वी', 'after physiotherapy': 'फिजिओथेरपीनंतर',
      'before activity': 'हालचालीपूर्वी', 'after activity': 'हालचालीनंतर',
      '30 min before food': 'जेवणापूर्वी 30 मिनिटे', '1 hour after food': 'जेवणानंतर 1 तासाने',
      'till review': 'पुढील तपासणीपर्यंत', 'continue': 'चालू ठेवा', 'long term': 'दीर्घकाळ',
    },
    hi: {
      'every 8 hours': 'हर 8 घंटे में', 'every 12 hours': 'हर 12 घंटे में',
      'on alternate days': 'एक दिन छोड़कर', 'weekly': 'हफ्ते में एक बार', 'twice weekly': 'हफ्ते में दो बार',
      'day 1-5 of cycle': 'माहवारी के 1 से 5 वें दिन', 'day 2-6 of cycle': 'माहवारी के 2 से 6 वें दिन', 'day 21 of cycle': 'माहवारी के 21 वें दिन',
      'continuously': 'लगातार', 'as needed': 'ज़रूरत अनुसार',
      'before physiotherapy': 'फिजियोथेरेपी से पहले', 'after physiotherapy': 'फिजियोथेरेपी के बाद',
      'before activity': 'गतिविधि से पहले', 'after activity': 'गतिविधि के बाद',
      '30 min before food': 'खाने से 30 मिनट पहले', '1 hour after food': 'खाने के 1 घंटे बाद',
      'till review': 'अगली जांच तक', 'continue': 'जारी रखें', 'long term': 'लंबे समय तक',
    },
  };

  const translatePhrase = (text: string): string => {
    const input = normalizeText(text);
    // English prints what the doctor chose, as written ("Every 8 hours", not "every 8 hours")
    if (!input || language === 'en') return input;
    const whole = WHOLE_PHRASES[language]?.[input.toLowerCase()];
    if (whole) return whole;
    // Word by word; untranslated words keep the case they were typed in
    let output = input;
    // Sort longest keys first to prevent 'day' matching inside 'days'
    const keys = Object.keys(phraseMap).sort((a, b) => b.length - a.length);
    for (const key of keys) {
      // Use word boundary \b to prevent 'day' replacing inside 'days'
      output = output.replace(new RegExp(`\\b${key}\\b`, 'gi'), phraseMap[key]);
    }
    return output;
  };

  const translateDuration = (duration: string): string => {
    const value = normalizeText(duration);
    if (!value) return '';
    return translatePhrase(value);
  };

  const medicineNameOf = (med: any): string =>
      normalizeText(med?.product || med?.brand_name || med?.generic_name);

  // Section headings reuse the translated labels, which carry a trailing colon
  const heading = (label: string | undefined, fallback: string) => normalizeText(label || fallback).replace(/:$/, '');

  // ── Derived info ──────────────────────────────────────────────────────────────

  const clinicName    = clinicInfo?.name    || '';
  const doctorName    = settings?.profile?.name   || doctorInfo?.name    || data?.doctor_name || '';
  const doctorSpec    = settings?.profile?.specialty || doctorInfo?.specialization || doctorInfo?.specialty || '';
  const doctorLicense = settings?.profile?.license   || doctorInfo?.registration_no || '';

  const patientName = normalizeText(patientInfo?.name || patientInfo?.patient_name || data?.patient_name);
  const age         = displayAge(patientInfo?.age, patientInfo?.date_of_birth);
  const gender      = normalizeText(patientInfo?.gender);
  const phone       = normalizeText(patientInfo?.phone || data?.patient_phone);
  const identifiers = [
    (patientInfo?.mrn || data?.patient_mrn) && `MRN ${patientInfo?.mrn || data?.patient_mrn}`,
    phone && `Ph ${groupPhone(phone)}`,
    normalizeText(patientInfo?.city),
  ].filter(Boolean).join(' · ');

  const vitals = data?.vitals ?? {};
  // Stored as "56.00" / "100.0" — print "56" / "100"
  const num = (v: unknown) => (Number.isFinite(Number(v)) ? String(Number(v)) : normalizeText(v));
  const vitalParts = [
    vitals.bp     && `BP ${vitals.bp} mmHg`,
    vitals.pulse  && `Pulse ${num(vitals.pulse)} /min`,
    vitals.spo2   && `SpO2 ${num(vitals.spo2)}%`,
    vitals.temp   && `Temp ${num(vitals.temp)} °F`,
    vitals.weight && `Wt ${num(vitals.weight)} kg`,
  ].filter(Boolean) as string[];

  // Signs first, then the findings as written (one line per system)
  const examination = [examSigns(vitals), normalizeText(data?.examination)].filter(Boolean).join('\n');

  const complaints = (data?.complaints ?? [])
      .filter((c: any) => c.complaint?.trim())
      .map((c: any) => {
        const d = normalizeText(c.duration);
        // Mid-sentence: "Cough since yesterday", not "Cough Since yesterday"
        return `${c.complaint.trim()}${d ? (d.toLowerCase().startsWith('since') ? ` since${d.slice(5)}` : ` since ${d}`) : ''}`;
      })
      .join(', ');

  const investigations = (data?.investigations ?? [])
      .map((i: any) => typeof i === 'string' ? i : (i?.investigation || ''))
      .filter((s: string) => s.trim())
      .join(', ');

  const medicines = (data?.medicines ?? []).filter((m: any) => medicineNameOf(m));

  const nextVisit = data?.next_visit?.date ? parseISO(String(data.next_visit.date).slice(0, 10)) : null;

  // Filled custom fields whose Settings position is one of `positions` (undefined = at the end)
  const customFieldsAfter = (positions: (string | undefined)[]) =>
      (settings?.prescriptionLayout?.customFields ?? [])
          .filter((cf: any) => cf.enabled && cf.label?.trim() && hasText(data?.custom_fields?.[cf.key]) && positions.includes(cf.after))
          .map((cf: any) => (
              <div key={cf.key} style={{ marginTop: '5mm' }}>
                <div className="p-muted" style={{ fontSize: '11px', fontWeight: 700 }}>{cf.label}</div>
                <div style={{ whiteSpace: 'pre-line' }}>{data.custom_fields[cf.key]}</div>
              </div>
          ));

  const factRow = (label: string, value: React.ReactNode) => (
      <><dt className="p-muted">{label}</dt><dd style={{ margin: 0 }}>{value}</dd></>
  );

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
      <div className="p-doc">
        <PrintStyles />

        <Letterhead
            headerConfig={headerConfig}
            clinic={{ name: clinicName, address: clinicInfo?.address, phone: clinicInfo?.phone, logo: clinicInfo?.logo, letterhead: clinicInfo?.letterhead }}
            right={doctorName && <>
              <b>Dr. {doctorName}</b>
              {doctorSpec && <div className="p-small p-muted">{doctorSpec}</div>}
              {doctorLicense && <div className="p-small p-muted">Reg. No. {doctorLicense}</div>}
            </>}
        />

        {/* Patient strip: who and when, on one line */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '8mm', padding: '3mm 0', borderBottom: '1px solid #999' }}>
          <div>
            <div style={{ fontSize: '15px', fontWeight: 700 }}>
              {patientName}
              {(age || gender) && <span className="p-muted" style={{ fontWeight: 400, fontSize: '13px' }}> · {[age, gender].filter(Boolean).join(' · ')}</span>}
            </div>
            {identifiers && <div className="p-muted" style={{ fontSize: '12px' }}>{identifiers}</div>}
          </div>
          <div style={{ textAlign: 'right', fontSize: '12px' }} className="p-muted">
            {t.date ?? 'Date'}
            <div style={{ color: '#111', fontSize: '14px', fontWeight: 700 }}>{formatDate(data?.date)}</div>
          </div>
        </div>

        {/* Allergies: printed only when the patient has some */}
        {hasText(patientInfo?.allergies) && (
            <div style={{ marginTop: '2.5mm', padding: '1.5mm 3mm', border: '1.5px solid #111', fontWeight: 700 }}>Allergies: {normalizeText(patientInfo.allergies)}</div>
        )}

        {(vitalParts.length > 0 || complaints || examination || hasText(data?.diagnosis)) && (
            <dl style={{ display: 'grid', gridTemplateColumns: '28mm 1fr', gap: '1.5mm 4mm', margin: '3.5mm 0 0' }}>
              {vitalParts.length > 0 && factRow('Vitals', vitalParts.join(' · '))}
              {complaints && factRow('Complaints', complaints)}
              {examination && factRow('Examination', <div style={{ whiteSpace: 'pre-line' }}>{examination}</div>)}
              {hasText(data?.diagnosis) && factRow('Diagnosis', <b>{normalizeText(data.diagnosis)}</b>)}
            </dl>
        )}
        {customFieldsAfter(['complaints', 'vitals', 'examination', 'diagnosis'])}

        {/* Medicines */}
        {medicines.length > 0 && (
            <>
              <div style={{ font: '700 26px/1 Georgia, serif', margin: '6mm 0 2mm' }}>℞</div>
              <table className="p-table">
                <thead>
                  <tr>
                    <th style={{ width: '6mm' }}>#</th>
                    <th>Medicine</th>
                    <th>Dose</th>
                    <th>When</th>
                    <th>Duration</th>
                    <th className="p-num">Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {medicines.map((med: any, index: number) => {
                    const when    = normalizeText(med?.taken_when ?? med?.when);
                    // No meaning line when it only repeats the dose ("SOS" under "SOS")
                    const meaningRaw = doseMeaning(normalizeText(med.dosage), t);
                    const meaning = meaningRaw.toLowerCase() === normalizeText(med.dosage).toLowerCase() ? '' : meaningRaw;
                    return (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>
                            <div style={{ fontWeight: 700, fontSize: '14px' }}>{medicineNameOf(med)}</div>
                            {hasText(med.generic_name) && <div className="p-muted p-small">{normalizeText(med.generic_name)}</div>}
                            {hasText(med.notes) && <div style={{ fontSize: '12px', marginTop: '1mm' }}>Note: {normalizeText(med.notes)}</div>}
                          </td>
                          <td>
                            {/* Short codes ("1-0-1", "½-0-½", "SOS") bold on one line; a written instruction wraps in the body font */}
                            {hasText(med.dosage) && (normalizeText(med.dosage).length <= 14
                                ? <div style={{ font: '700 15px/1.2 "Courier New", monospace', whiteSpace: 'nowrap' }}>{normalizeText(med.dosage)}</div>
                                : <div style={{ fontSize: '13px' }}>{normalizeText(med.dosage)}</div>)}
                            {/* Hindi / Marathi: Devanagari at 11px grey is hard to read, so full size and black */}
                            {meaning && (language === 'en'
                                ? <div className="p-muted" style={{ fontSize: '11px' }}>{meaning}</div>
                                : <div style={{ fontSize: '13px' }}>{meaning}</div>)}
                          </td>
                          <td>{when ? translatePhrase(when) : '—'}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>{hasText(med.duration) ? translateDuration(med.duration) : '—'}</td>
                          <td className="p-num">{med.quantity != null && med.quantity !== '' ? String(med.quantity) : ''}</td>
                        </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
        )}

        {customFieldsAfter(['medicines'])}

        {investigations && (
            <div style={{ marginTop: '5mm' }}>
              <div className="p-muted" style={{ fontSize: '11px', fontWeight: 700 }}>{heading(t.investigations, 'Tests')}</div>
              {investigations}
            </div>
        )}
        {customFieldsAfter(['investigations'])}

        {hasText(data?.advice) && (
            <div style={{ marginTop: '5mm' }}>
              <div className="p-muted" style={{ fontSize: '11px', fontWeight: 700 }}>{heading(t.advice, 'Advice')}</div>
              <div style={{ whiteSpace: 'pre-line' }}>{data.advice}</div>
            </div>
        )}

        {/* Custom fields left at the end (Settings → Prescription layout), with "after Quick notes" (notes aren't printed) */}
        {customFieldsAfter([undefined, 'advice', 'notes'])}

        {nextVisit && isValid(nextVisit) && (
            <div style={{ alignSelf: 'flex-start', marginTop: '5mm', padding: '2mm 4mm', border: '1.5px solid #111', fontSize: '14px' }}>
              {heading(t.nextVisit, 'Next visit')}: <b>{format(nextVisit, 'EEE, dd MMM yyyy')}</b>
              {data.next_visit.time ? ` at ${DateUtil.formatDateForType(data.next_visit.time, 'time-only')}` : ''}
            </div>
        )}
        {customFieldsAfter(['next-visit'])}

        <SignatureBlock image={signatureImage}>
          {settings?.profile?.signature
              ? settings.profile.signature.split('\n').map((line: string, i: number) => (
                  <div key={i} style={i === 0 ? { fontWeight: 700, fontSize: '13.5px' } : undefined}>{line}</div>
              ))
              : <>
                {doctorName && <b>Dr. {doctorName}</b>}
                {doctorSpec && <div>{doctorSpec}</div>}
                {doctorLicense && <div>Reg. No. {doctorLicense}</div>}
              </>
          }
        </SignatureBlock>

        <PrintFooter left={[clinicName, 'Prescription'].filter(Boolean).join(' · ')} printedOn={formatDateTime(new Date())} />
      </div>
  );
}
